/**
 * The concierge (phase 1, 04-AGENT, 10 §3): the case ledger the console shows, grants and settings,
 * the guest's conversation and its SSE events, Try it, and AI cost. Also the vendor-neutral LLM types
 * (10 §2.1) that `@becca/agent` and the API share, so ledger payloads and `ai_run` rows agree.
 */
import { z } from 'zod';
import { Actor, DateString, Id, Instant, NonNegativeCents } from './common.js';
import { DEFAULT_SETTINGS } from './content.js';
// ── LLM providers (10 §2.1) ───────────────────────────────────────────────
/** Which adapter produced a turn. `scripted`/`recording` appear in tests and evals only. */
export const ProviderId = z.enum(['anthropic', 'openai_compatible', 'scripted', 'recording']);
/** Tokens for one model step, normalised across vendors. `cachedInput` is 0 without prompt caching. */
export const LlmUsage = z.object({
    input: z.number().int().min(0),
    cachedInput: z.number().int().min(0),
    output: z.number().int().min(0),
});
/** Why a step stopped: it wants tool calls, it finished, it hit the output cap, or the model refused. */
export const LlmStop = z.enum(['calls', 'done', 'truncated', 'refused']);
export const ProviderCaps = z.object({
    tools: z.boolean(),
    parallelTools: z.boolean(),
    strictSchemas: z.boolean(),
    promptCaching: z.boolean(),
    streaming: z.boolean(),
});
/**
 * AI spend in cents. Fractional on purpose: a turn on a small model often costs under a cent, and
 * rounding every `ai_run` would make the monthly total wrong. `0` for a local model.
 */
export const CostCents = z.number().min(0).max(1_000_000_000);
// ── Channels, scenes, facts ───────────────────────────────────────────────
/** Channels a tool grant can be narrowed to (`send:web`, `send:email`, `send:sms`). */
export const AgentChannel = z.enum(['web', 'email', 'sms']);
/** The channels a proactive touch can go out on (phase 2): the web chat only ever answers. */
export const TouchChannel = AgentChannel.extract(['email', 'sms']);
/** The guest app's scenes: the only targets `show` may name (04 §4.4). */
export const GuestScene = z.enum(['start', 'spaces', 'dates', 'packages', 'plan', 'reserve', 'talk']);
/** A fact id from a tool result in this case (`f1`, `f2`, …), cited by what the concierge says. */
export const FactId = z.string().regex(/^f[1-9]\d*$/, 'Expected a fact id like f12');
// ── The case and its ledger (04 §2, §6) ───────────────────────────────────
export const CaseEventKind = z.enum([
    'prompt',
    'model_text',
    'reasoning',
    'tool_call',
    'policy',
    'tool_result',
    'draft',
    'approval',
    'staff_instruction',
    'staff_reply',
    'compaction',
    'end',
]);
/**
 * A `policy` row's `action`: the call ran, was parked for an approval, was refused, was recorded as a
 * draft (`observe`), or — with no call — code reminded the model that a turn ends with a control tool.
 */
export const PolicyAction = z.enum(['run', 'park', 'refuse', 'draft', 'remind']);
/**
 * One append-only ledger row. `payload` is kind-specific and owned by `@becca/agent` (e.g. a
 * `tool_call` carries `{ callId, name, args, describe }`, plus `resolved` and `handles` when its args
 * named rows by handle; a `policy` row `{ callId, action, reason }`); the token and cost columns are
 * set on rows that record a model step.
 */
export const CaseEvent = z.object({
    seq: z.number().int().min(0),
    kind: CaseEventKind,
    at: Instant,
    payload: z.record(z.string(), z.unknown()),
    tokensIn: z.number().int().min(0).nullish(),
    tokensOut: z.number().int().min(0).nullish(),
    costCents: CostCents.nullish(),
    provider: ProviderId.nullish(),
    model: z.string().nullish(),
});
/** Why a case opened or resumed (04 §2). Phase 1 uses `new_lead`, `guest_message:web`, `staff_input`, `handed_back`. */
export const CaseTrigger = z.enum([
    'new_lead',
    'guest_message:web',
    'guest_message:email',
    'guest_message:sms',
    'review',
    'hold_expiring',
    'hold_expired',
    'date_taken',
    'booking_event',
    'tour_upcoming',
    'tour_done',
    'tour_no_show',
    'staff_sms',
    'missed_call',
    'date_freed',
    'staff_input',
    'approval_decided',
    'handed_back',
]);
export const CaseStatus = z.enum(['open', 'parked', 'closed']);
/** What a parked case waits on (04 §6.3): a staff answer, an approval click, or the guest's reply. */
export const CaseParkedOn = z.enum(['staff', 'approval', 'guest']);
export const CaseSummary = z.object({
    id: Id,
    leadId: Id,
    status: CaseStatus,
    /** Set while `status = parked`. */
    parkedOn: CaseParkedOn.nullish(),
    turns: z.number().int().min(0),
    calls: z.number().int().min(0),
    writes: z.number().int().min(0),
    costCents: CostCents,
    openedBy: CaseTrigger,
    /** Set once closed: the `done` summary, a limit hit, park TTL, or a staff action. */
    closedReason: z.string().max(2000).nullish(),
});
// ── Autonomy (04 §5) ──────────────────────────────────────────────────────
export const ToolGrantLevel = z.enum(['observe', 'approve', 'auto']);
/** A tool name as the model sees it (`send`, `update_plan`). */
export const ToolName = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/, 'Expected a tool name like update_plan');
/**
 * The phase 4 tools (04 §4.3, 13 §4): `tour_slots` (read), `book_tour` and `cancel_tour` (tier 3,
 * provenance from `tour_slots` / this lead's tours, launch `auto`), `record_debrief` (staff turns only).
 * `notify_staff` exists since phase 2. Names only: `@becca/agent` defines the tools.
 */
export const TourToolName = z.enum(['tour_slots', 'book_tour', 'cancel_tour', 'record_debrief']);
const toolGrantFields = {
    tool: ToolName,
    /** Null: the grant covers every channel of the tool (and tools without channels). */
    channel: AgentChannel.nullable(),
    level: ToolGrantLevel,
};
export const ToolGrant = z.object({ ...toolGrantFields, setBy: Actor, setAt: Instant });
export const ToolGrantInput = z.object({
    ...toolGrantFields,
    channel: AgentChannel.nullable().default(null),
});
/** The default `askStaffWhen`: what a venue would not want the concierge to decide on its own. */
export const DEFAULT_ASK_STAFF_WHEN = [
    'A party that no space seats, or a request for the whole restaurant: say the largest option is a full buyout and what it seats, and ask whether a buyout could work, leaving their plan as it is until the team answers. Do not decide yourself that standing or splitting rooms would do.',
];
/**
 * Per-venue switches and ceilings staff edit (04 §2 limits, §5.4 kill switches). Budgets: a case lives
 * for weeks of follow-up, so calls and writes are budgeted per turn (`maxCallsPerTurn`,
 * `maxWritesPerTurn`); over its life a case is capped by cost (`maxCaseCents`) and by the turns a guest
 * started (`maxGuestTurns`). Reviews and other code-woken turns cost money but never use up turns.
 */
export const AgentSettingsInput = z.object({
    /** Global switch; off → the guest app shows the ask-us form. */
    enabled: z.boolean(),
    /** Mirrors `settings.monthlyAiCapCents`. */
    monthlyCapCents: z.number().int().min(0),
    /**
     * The case's lifetime cost cap in cents (every turn and approved run of the case): at the cap code hands
     * the lead to staff. With `maxGuestTurns`, the only per-case limits; calls and writes are per turn.
     */
    maxCaseCents: z.number().int().min(0),
    /**
     * Deprecated (phase 2/3 fix pass), kept on the wire and ignored: was the case's lifetime turn cap, which
     * counted reviews and handed a lead off after weeks of follow-up. See `maxGuestTurns`.
     */
    maxTurns: z.number().int().min(1).max(100),
    /** Deprecated, kept on the wire and ignored: was a per-case lifetime call cap. See `maxCallsPerTurn`. */
    maxCalls: z.number().int().min(1).max(500),
    /** Deprecated, kept on the wire and ignored: was a per-case lifetime write cap. See `maxWritesPerTurn`. */
    maxWrites: z.number().int().min(0).max(100),
    /**
     * Turns of one case a guest started (`new_lead`, `guest_message:*`, `missed_call`); reviews, holds
     * expiring, approvals decided and staff turns do not count. Past it code hands the lead to staff.
     * Omitted on a write → 20.
     */
    maxGuestTurns: z.number().int().min(1).max(500).default(20),
    /**
     * Tool calls the model may make in one turn (run, drafted, parked or refused; ending calls are exempt).
     * Every turn — a review weeks later too — starts with a fresh budget. Omitted on a write → 30.
     */
    maxCallsPerTurn: z.number().int().min(1).max(500).default(30),
    /** Tier ≥ 2 calls that may run at `auto` in one turn (each turn fresh). Omitted on a write → 6. */
    maxWritesPerTurn: z.number().int().min(0).max(100).default(6),
    parkTtlDays: z.number().int().min(1).max(90),
    /**
     * Wall clock for one concierge turn (10 §4: 60 s on the web channel). A cold local model can need
     * more on its first step; the dev seed sets 120 s. Omitted on a write → the default.
     */
    turnWallClockMs: z.number().int().min(10_000).max(600_000).default(60_000),
    /**
     * Situations where the concierge tells the guest what it can and asks the events team (`ask_staff`)
     * instead of deciding itself; one plain sentence each, rendered into the system prompt. Omitted on a
     * write → the default.
     */
    askStaffWhen: z
        .array(z.string().trim().min(1).max(500))
        .max(20)
        .default(() => [...DEFAULT_ASK_STAFF_WHEN]),
    /**
     * Phase 2 (11 §4.2): a `touch` must name something recorded about this lead (a priority, the occasion,
     * the date, the space), else the send policy refuses it `NOT_PERSONAL`. Omitted on a write → on.
     */
    personalisationRule: z.boolean().default(true),
});
/** The settings plus the configured model, read-only (env, 10 §0); null provider = no LLM configured. */
export const AgentSettings = AgentSettingsInput.extend({
    provider: ProviderId.nullable(),
    model: z.string().nullable(),
    smallModel: z.string().nullable(),
});
export const DEFAULT_AGENT_SETTINGS = {
    enabled: false,
    monthlyCapCents: DEFAULT_SETTINGS.monthlyAiCapCents,
    maxCaseCents: 150,
    maxTurns: 12,
    maxCalls: 30,
    maxWrites: 6,
    maxGuestTurns: 20,
    maxCallsPerTurn: 30,
    maxWritesPerTurn: 6,
    parkTtlDays: 7,
    turnWallClockMs: 60_000,
    askStaffWhen: [...DEFAULT_ASK_STAFF_WHEN],
    personalisationRule: true,
};
// ── Follow-up (phase 2, 11 §2.1, 04 §5.3) ─────────────────────────────────
/**
 * The follow-up state of one channel of a lead, computed by the API from its messages and the venue's
 * ceilings. A read model: returned by the `lead()` tool, `GET /s/leads/:id/touches` and the case state.
 */
export const TouchLedger = z.object({
    channel: TouchChannel,
    /** Outbound touches since the guest last wrote or called, on this channel. */
    unansweredCount: z.number().int().min(0),
    lastOutboundAt: Instant.nullable(),
    lastInboundAt: Instant.nullable(),
    /**
     * The earliest instant the next touch passes the gap ceiling (quiet hours are applied at send time, not
     * here). Null when `unansweredCount` has reached `maxUnansweredTouches`: nothing more until the guest
     * answers.
     */
    nextAllowedAt: Instant.nullable(),
    /** Every fact id cited by an outbound touch on any channel: a new touch must cite one not in this list. */
    factIdsCitedSoFar: z.array(FactId),
});
// ── The guest's conversation (web channel) ────────────────────────────────
export const ConciergeRole = z.enum(['guest', 'concierge', 'staff', 'system']);
export const ConciergeMessage = z.object({
    id: Id,
    role: ConciergeRole,
    text: z.string(),
    at: Instant,
    facts: z.array(FactId).optional(),
    /** The scene the guest was on when they wrote it, or the one the concierge moved them to. */
    scene: GuestScene.optional(),
});
/** `off`: the agent is disabled or no LLM is configured — show the ask-us form. */
export const ConciergeState = z.enum(['idle', 'thinking', 'waiting_staff', 'handed_off', 'off']);
/**
 * One event on the `POST /g/concierge` stream. On the wire each is an SSE frame
 * `event: <type>\ndata: <this object as JSON>\n\n`. Order: `message_start`, `text`*, (`show` |
 * `question_asked` | `handed_off`)*, `message_end`, … `done` last; `error` may replace the rest.
 */
export const ConciergeSseEvent = z.discriminatedUnion('type', [
    z.object({ type: z.literal('message_start'), id: Id }),
    z.object({ type: z.literal('text'), delta: z.string() }),
    z.object({
        type: z.literal('show'),
        scene: GuestScene,
        /** Validated per scene by the guest app; unknown keys are ignored there. */
        params: z.record(z.string(), z.unknown()),
    }),
    /** After `ask_staff`: the guest hears back by email; `eta` when staff availability gives one. */
    z.object({ type: z.literal('question_asked'), eta: Instant.optional() }),
    z.object({ type: z.literal('handed_off') }),
    z.object({ type: z.literal('message_end'), id: Id, facts: z.array(FactId) }),
    z.object({ type: z.literal('error'), code: z.string().regex(/^[A-Z][A-Z0-9_]*$/) }),
    z.object({ type: z.literal('done') }),
]);
// ── Try it and evals ──────────────────────────────────────────────────────
/** A staff sandbox: a synthetic lead (`source = try_it`) and its guest token, deleted with the session. */
export const TryItSession = z.object({
    sessionId: Id,
    /** The synthetic lead, so the console can open its case drawer. */
    leadId: Id,
    /** A guest magic token for the synthetic lead: the console calls `/g/concierge` with it. */
    token: z.string(),
});
export const EvalCandidateReason = z.enum(['wrong_fact', 'wrong_tone', 'other']);
export const EvalCandidate = z.object({
    id: Id,
    caseId: Id,
    eventSeq: z.number().int().min(0),
    reason: EvalCandidateReason,
    note: z.string(),
    createdBy: Actor,
    createdAt: Instant,
});
// ── Cost report ───────────────────────────────────────────────────────────
const costRow = {
    costCents: CostCents,
    tokens: LlmUsage,
    /** Model steps (`ai_run` rows). */
    runs: z.number().int().min(0),
};
/** AI spend over `[from, to]` (venue-local dates): per day × provider × model, and per lead. */
export const AiCostReport = z.object({
    from: DateString,
    to: DateString,
    total: z.object(costRow),
    /** The venue's monthly cap, for comparison. */
    monthlyCapCents: NonNegativeCents,
    days: z.array(z.object({ date: DateString, provider: ProviderId, model: z.string(), ...costRow })),
    leads: z.array(z.object({ leadId: Id, leadLabel: z.string(), ...costRow })),
});
//# sourceMappingURL=agent.js.map